<script setup lang="ts">
import { whenever } from "@vueuse/core";
import { onPointerDownOutside } from "ls/composables/onPointerDownOutside";
import { nextTick, useTemplateRef } from "vue";

const isMaximized = defineModel<boolean>();

const container = useTemplateRef("container");
const content = useTemplateRef("content");

// move focus inside container when it is maximized
// so that Escape key can be used to exit maximized mode
whenever(isMaximized, () => {
  void nextTick(() => {
    container.value?.focus();
  });
}, { immediate: true });

function minimize() {
  isMaximized.value = false;
}

onPointerDownOutside(content, () => {
  minimize();
});

const dialogOverlay = "fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 p-2 focus:outline-hidden sm:px-6 sm:py-8 lg:px-8 lg:py-16";
</script>

<template>
  <div
    :class="{
      'size-full': !isMaximized,
      'fixed top-0 left-0 z-10': isMaximized,
    }"
  >
    <div
      v-if="isMaximized"
      :class="dialogOverlay"
    />
    <div
      :class="{
        'size-full': !isMaximized,
        'fixed inset-0 w-screen transform-gpu overflow-y-auto pt-0': isMaximized,
      }"
    >
      <div
        ref="container"
        :tabindex="isMaximized ? -1 : undefined"
        :class="{
          'size-full': !isMaximized,
          'grid min-h-full grid-rows-1 justify-items-center p-20 outline-hidden': isMaximized,
        }"
        @keydown.esc.stop="minimize"
      >
        <div
          ref="content"
          :class="{
            'size-full': !isMaximized,
            'w-full min-w-0 overflow-hidden rounded-sm shadow-lg': isMaximized,
          }"
        >
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
